// ── thresholds-helpers: the LIVE threshold-settings model (FR-SET) ──────────
// The Settings/Thresholds surface now reads the NOW-LIVE GET /settings/thresholds
// (view.settings, ?orgId, bare { thresholds:[…] }) and SA/Admin upsert each
// threshold via POST /settings/thresholds (manage.settings + a UUID
// Idempotency-Key). A threshold the backend has NOT stored yet renders a neutral
// "not set" placeholder — no value is fabricated; the toast fires only AFTER the
// backend confirms.

import { canAccessThresholdSettings, canEditThresholds } from "../../domain/logs-and-exports";
import type { CoachingThresholdDto } from "../../services/coaching-readiness-dtos";

export { canAccessThresholdSettings, canEditThresholds };

// The known threshold knobs the PRD describes (percentage 0–100). These are the
// SHAPE the surface manages; the LIVE value (if any) is merged in from the read.
// A field with no live row renders a neutral placeholder ("not set"), never a
// fabricated stored value.
export interface ThresholdField {
  key: string;
  label: string;
  description: string;
  /** Display-only placeholder shown when the backend has no stored value yet. */
  placeholder: string;
}

export const THRESHOLD_FIELDS: ThresholdField[] = [
  {
    key: "attendance_pass",
    label: "Attendance pass threshold",
    description: "Minimum attendance percentage for a participant to be considered on-track.",
    placeholder: "not set"
  },
  {
    key: "progress_complete",
    label: "Progress completion threshold",
    description: "Course-progress percentage that marks a participant complete.",
    placeholder: "not set"
  },
  {
    key: "capstone_pass",
    label: "Capstone pass threshold",
    description: "Minimum capstone evaluation percentage required to pass.",
    placeholder: "not set"
  }
];

/** A field merged with its live value (if the backend has stored one). */
export interface MergedThresholdField extends ThresholdField {
  /** The live stored row, or null when the backend has no value yet (not set). */
  stored: CoachingThresholdDto | null;
}

/**
 * Merge the known fields with the live read rows. We only ever display a value
 * the backend returned for a known key — an unknown key is ignored (no
 * fabrication), and a known field with no row is "not set".
 */
export function mergeThresholdFields(
  rows: CoachingThresholdDto[] | undefined
): MergedThresholdField[] {
  const byKey = new Map((rows ?? []).map((row) => [row.key, row] as const));
  return THRESHOLD_FIELDS.map((field) => ({ ...field, stored: byKey.get(field.key) ?? null }));
}

/** Validate a threshold percentage (0–100 integer). */
export function isValidThresholdPercent(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 100;
}

/** Clamp a percentage to the 0–100 range. */
export function clampThresholdPercent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}
